'use client'

import { Heart, ImageIcon, Play, Repeat2, VideoIcon } from 'lucide-react'

import { Logo } from '@/components/Logo/Logo'
import { Media } from '@/components/Media'
import { useImgOnloadFallback } from '@/hooks/useImgOnloadFallback'
import { cn } from '@/utilities/ui'

export type TweetUser = {
  id: string
  name: string
  username: string
  profile_image_url?: string
}

export type TweetMedia = {
  media_key: string
  type: 'photo' | 'video' | 'animated_gif'
  url?: string
  preview_image_url?: string
  width?: number
  height?: number
}

export type TweetUrlEntity = {
  start: number
  end: number
  url: string
  expanded_url: string
  display_url: string
  title?: string
  description?: string
  images?: { url: string; width?: number; height?: number }[]
  unwound_url?: string
}

export type TweetData = {
  id: string
  text: string
  author_id?: string
  profile_image_url?: string
  created_at?: string
  public_metrics?: {
    like_count: number
    retweet_count: number
    reply_count: number
    quote_count: number
  }
  attachments?: {
    media_keys?: string[]
  }
  entities?: {
    urls?: TweetUrlEntity[]
    mentions?: { start: number; end: number; username: string }[]
    hashtags?: { start: number; end: number; tag: string }[]
  }
}

const MediaFallbackElement = ({
  type,
  className,
}: {
  type: 'photo' | 'video' | 'animated_gif'
  className?: string
}) => {
  const iconClassName =
    'w-[60%] h-auto aspect-square text-neutral-300/50 block absolute right-4 -bottom-[20%] opacity-60 z-0'

  return (
    <div
      className={cn(
        'absolute inset-0 flex flex-col items-center justify-center bg-neutral-200/50 p-2.5 transition-opacity',
        className,
      )}
    >
      <div className="relative z-2">
        <Logo className="mb-2 block h-8 w-auto opacity-50" />
        <span className="block font-body text-xs font-semibold text-muted-foreground/50">
          {type !== 'photo' ? 'Video' : 'Image'} cannot be displayed
        </span>
      </div>
      {type === 'photo' && <ImageIcon className={iconClassName} />}
      {(type === 'video' || type === 'animated_gif') && <VideoIcon className={iconClassName} />}
    </div>
  )
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function tweetUrl(username: string, tweetId: string) {
  return `https://x.com/${username}/status/${tweetId}`
}

function isMediaUrl(displayUrl: string) {
  return displayUrl.startsWith('pic.x.com') || displayUrl.startsWith('pic.twitter.com')
}

/** Use our image proxy for Twitter CDN URLs so the browser doesn't label them as "Tracking". */
function mediaSrc(url: string): string {
  try {
    const u = new URL(url)
    if (u.hostname === 'pbs.twimg.com' || u.hostname === 'abs.twimg.com') {
      return `/api/proxy-image?url=${encodeURIComponent(url)}`
    }
  } catch {
    /* ignore */
  }
  return url
}

const SENTENCE_ENDERS = new Set([
  '.',
  '!',
  '?',
  ':',
  '"',
  "'",
  ')',
  ']',
  '\u201D',
  '\u2019',
  '\u2026',
])

function isTruncated(tweet: TweetData): number | null {
  const urls = tweet.entities?.urls
  if (!urls?.length) return null

  const last = [...urls].sort((a, b) => b.start - a.start)[0]
  if (!last.url.includes('t.co/')) return null

  const after = tweet.text.slice(last.end).trim()
  if (after !== '') return null

  const before = tweet.text.slice(0, last.start).trimEnd()
  if (before.length === 0) return null
  if (SENTENCE_ENDERS.has(before.slice(-1))) return null

  return last.start
}

function renderText(tweet: TweetData): React.ReactNode[] {
  const { text, entities } = tweet
  if (!entities) return [text]

  const truncatedAt = isTruncated(tweet)

  const parts: { start: number; end: number; node: React.ReactNode | null }[] = []

  entities.urls?.forEach((u) => {
    if (isMediaUrl(u.display_url)) {
      parts.push({ start: u.start, end: u.end, node: null })
      return
    }
    parts.push({
      start: u.start,
      end: u.end,
      node: (
        <a
          key={`url-${u.start}`}
          href={u.expanded_url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-body text-evergreen-600 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {u.display_url}
        </a>
      ),
    })
  })

  entities.mentions?.forEach((m) => {
    parts.push({
      start: m.start,
      end: m.end,
      node: (
        <a
          key={`mention-${m.start}`}
          href={`https://x.com/${m.username}`}
          target="_blank"
          rel="noopener noreferrer"
          className="font-body text-evergreen-600 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          @{m.username}
        </a>
      ),
    })
  })

  entities.hashtags?.forEach((h) => {
    parts.push({
      start: h.start,
      end: h.end,
      node: (
        <a
          key={`hash-${h.start}`}
          href={`https://x.com/hashtag/${h.tag}`}
          target="_blank"
          rel="noopener noreferrer"
          className="font-body text-evergreen-600 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          #{h.tag}
        </a>
      ),
    })
  })

  parts.sort((a, b) => a.start - b.start)

  const result: React.ReactNode[] = []
  let cursor = 0
  const codePoints = [...text]

  parts.forEach((p) => {
    if (p.start > cursor) {
      let segment = codePoints.slice(cursor, p.start).join('')
      if (truncatedAt !== null && p.start === truncatedAt) {
        segment = segment.trimEnd() + '\u2026'
      }
      result.push(segment)
    }
    if (p.node) result.push(p.node)
    cursor = p.end
  })

  if (cursor < codePoints.length) {
    result.push(codePoints.slice(cursor).join(''))
  }

  return result
}

function LinkPreview({ url }: { url: TweetUrlEntity }) {
  let domain: string
  try {
    domain = new URL(url.expanded_url).hostname.replace(/^www\./, '')
  } catch {
    domain = url.display_url
  }
  const image = url.images?.[0]?.url
  const { imageLoaded } = useImgOnloadFallback({ imageURL: image ?? '' })

  return (
    <a
      href={url.expanded_url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="mt-3 block overflow-hidden rounded-xl border border-border transition-colors hover:bg-accent-foreground/25"
    >
      <div className="relative flex overflow-hidden rounded-t-xl">
        <MediaFallbackElement
          type="photo"
          className={cn(
            'block h-36 w-full rounded-b-none object-cover',
            imageLoaded ? 'pointer-events-none opacity-0' : 'opacity-100',
          )}
        />
        {image && (
          <Media
            resource={image}
            alt=""
            htmlelement={null}
            width={100}
            height={100}
            pictureClassName={cn(
              'block h-36 w-full object-cover transition-opacity',
              imageLoaded ? 'opacity-100' : 'opacity-0',
            )}
            imgClassName="h-36 w-full object-cover"
            loading="lazy"
          />
        )}
      </div>
      <div className="bg-background p-3">
        <p className="font-body text-xs text-muted-foreground">{domain}</p>
        {url.title && (
          <p className="line-clamp-2 font-body text-sm font-medium text-foreground">{url.title}</p>
        )}
        {url.description && (
          <p className="mt-0.5 line-clamp-2 font-body text-xs text-muted-foreground">
            {url.description}
          </p>
        )}
      </div>
    </a>
  )
}

function removeNormalFromProfileImageUrl(url: string): string {
  return url.replace('_normal', '')
}

export function TweetCard({
  tweet,
  author,
  media,
}: {
  tweet: TweetData
  author?: TweetUser
  media?: TweetMedia[]
}) {
  const link = author ? tweetUrl(author.username, tweet.id) : `https://x.com/i/status/${tweet.id}`

  const ogUrls =
    tweet.entities?.urls?.filter(
      (u) => !isMediaUrl(u.display_url) && (u.title || u.images?.length),
    ) ?? []

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() => window.open(link, '_blank', 'noopener')}
      onKeyDown={(e) => {
        if (e.key === 'Enter') window.open(link, '_blank', 'noopener')
      }}
      className="mb-4 cursor-pointer break-inside-avoid rounded-2xl border border-border bg-card p-4 transition-colors hover:bg-accent-foreground/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen-500 focus-visible:ring-offset-0"
    >
      {author && (
        <div className="mb-3 flex items-center gap-2">
          {author.profile_image_url && (
            // <img
            //   src={mediaSrc(removeNormalFromProfileImageUrl(author.profile_image_url))}
            //   alt=""
            //   className="block aspect-square size-10 rounded-xl object-cover"
            // />
            <Media
              resource={mediaSrc(removeNormalFromProfileImageUrl(author.profile_image_url))}
              context="thumbnail"
              alt=""
              htmlelement={null}
              width={40}
              height={40}
              pictureClassName="block aspect-square size-10 object-cover"
              imgClassName="size-10 rounded-xl object-cover"
              unoptimized
            />
          )}
          {!author.profile_image_url && (
            <div className="flex size-10 items-center justify-center rounded-xl bg-accent">
              <Logo className="size-8 invert-100" />
            </div>
          )}
          <div className="flex flex-col leading-tight">
            <span className="font-body text-sm font-semibold text-foreground">{author.name}</span>
            <span className="font-body text-xs text-muted-foreground">@{author.username}</span>
          </div>
        </div>
      )}

      <p className="font-body text-sm wrap-break-word whitespace-pre-wrap text-foreground">
        {renderText(tweet)}
      </p>

      {ogUrls.map((u) => (
        <LinkPreview key={u.start} url={u} />
      ))}

      {media && media.length > 0 && (
        <div
          className={cn(
            'mt-3 grid gap-1 overflow-hidden rounded-lg',
            media.length === 1 && 'grid-cols-1',
            media.length === 2 && 'grid-cols-2',
            media.length >= 3 && 'grid-cols-2',
          )}
        >
          {media.map((m) => (
            <div key={m.media_key} className="relative">
              {m.type === 'photo' && m.url && (
                // <img
                //   src={mediaSrc(m.url)}
                //   alt=""
                //   className="block h-auto w-full rounded-lg object-cover"
                // />
                <Media
                  resource={mediaSrc(m.url)}
                  alt=""
                  context="thumbnail"
                  htmlelement={null}
                  pictureClassName="block h-auto w-full rounded-lg object-cover"
                  imgClassName="h-auto w-full rounded-lg object-cover"
                  width={100}
                  height={100}
                  loading="lazy"
                  unoptimized
                />
              )}
              {(m.type === 'video' || m.type === 'animated_gif') && m.preview_image_url && (
                <div className="relative">
                  {/* <img
                    src={mediaSrc(m.preview_image_url)}
                    alt=""
                    className="block h-auto w-full rounded-lg object-cover"
                  /> */}
                  <Media
                    resource={mediaSrc(m.preview_image_url)}
                    alt=""
                    context="thumbnail"
                    htmlelement={null}
                    pictureClassName="block h-auto w-full rounded-lg object-cover"
                    imgClassName="h-auto w-full rounded-lg object-cover"
                    width={100}
                    height={100}
                    loading="lazy"
                    unoptimized
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex size-12 items-center justify-center rounded-2xl bg-neutral-900/60 text-white">
                      <Play className="ml-0.5 size-5 fill-current" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tweet.public_metrics && (
        <div className="mt-3 flex items-center gap-4 font-body text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Heart className="size-3.5" />
            {formatCount(tweet.public_metrics.like_count)}
          </span>
          <span className="flex items-center gap-1">
            <Repeat2 className="size-3.5" />
            {formatCount(tweet.public_metrics.retweet_count)}
          </span>
          {tweet.created_at && (
            <span className="ml-auto font-body text-xs text-muted-foreground">
              {formatDate(tweet.created_at)}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
